import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { PaymentOption } from '@client/components/ShopInsuranceModal';
import { ShopInsuranceQuotesList } from '@client/components/shop-insurance/ShopInsuranceQuotesList';
import type { TraditionalLTCQuoteWithROI } from '@client/hooks/useTraditionalLTCQuotesWithROI';
import type { Quote } from '@client/model';
import { cn } from '@/lib/utils';

interface QuoteWithROI extends Quote {
  roi?: number;
  roiValues?: any;
  formattedROI?: string;
}

interface ShopInsuranceTabContentProps {
  underwritingFetchStarted: boolean;
  underwritingFetchComplete: boolean;
  filteredAdjustedQuotes: QuoteWithROI[];
  filteredTraditionalLTCQuotesWithEligibility: TraditionalLTCQuoteWithROI[];
  underwritingResults: Map<string, 'eligible' | 'ineligible' | 'unsure' | null>;
  expandedIndices: number[];
  selectedTab: ShopInsuranceTab;
  clientPartner: any;
  premiumValue: number;
  paymentOption: PaymentOption;
  advisorId: string;
  isQuoteSelected: (quote: any) => { isSelected: boolean; fundingSourceId: string | null };
  handleCardToggle: (index: number) => void;
  handleViewUnderwritingDetails: (quoteId: string, productName: string) => void;
  onClose: () => void;
}

export function ShopInsuranceTabContent({
  underwritingFetchStarted,
  underwritingFetchComplete,
  filteredAdjustedQuotes,
  filteredTraditionalLTCQuotesWithEligibility,
  underwritingResults,
  expandedIndices,
  selectedTab,
  clientPartner,
  premiumValue,
  paymentOption,
  advisorId,
  isQuoteSelected,
  handleCardToggle,
  handleViewUnderwritingDetails,
  onClose,
}: ShopInsuranceTabContentProps) {
  if (underwritingFetchStarted && !underwritingFetchComplete) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center gap-4 py-12">
        <LoadingSpinner testId="shop-insurance-modal-loading-spinner" />
        <p className="text-sm text-gray-600">Checking eligibility for available products...</p>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-4">
      <div
        data-testid="shop-insurance-quotes-list"
        className={cn('flex w-full flex-col gap-4 lg:h-[calc(100vh-320px)] lg:overflow-y-auto')}
      >
        <ShopInsuranceQuotesList
          filteredAdjustedQuotes={filteredAdjustedQuotes}
          filteredTraditionalLTCQuotesWithEligibility={filteredTraditionalLTCQuotesWithEligibility}
          underwritingResults={underwritingResults}
          expandedIndices={expandedIndices}
          selectedTab={selectedTab}
          clientPartner={clientPartner}
          premiumValue={premiumValue}
          paymentOption={paymentOption}
          advisorId={advisorId}
          isQuoteSelected={isQuoteSelected}
          handleCardToggle={handleCardToggle}
          handleViewUnderwritingDetails={handleViewUnderwritingDetails}
          onClose={onClose}
        />
      </div>
    </div>
  );
}
